/*
INTELLECTUAL PROPERTY NOTICE
© Kaleida Ltd and/or Orbit Tickets Ltd. All rights reserved.
This file contains a combination of:
- Protected Intellectual Property owned by Kaleida Ltd; and
- Custom / Included Intellectual Property owned by the Customer,
and forms part of an application developed by Kaleida within the Platform.
Protected Intellectual Property may only be used as incorporated within applications developed by Kaleida within the Platform and must not be used, reproduced, adapted, or incorporated into any application developed by or on behalf of the Customer (being any application not developed by Kaleida), whether or not such application forms part of the Platform.
Custom / Included Intellectual Property may be used, reproduced, adapted, and incorporated into any application, system, or product developed by or on behalf of the Customer, whether or not such application forms part of the Platform, subject always to the terms of the agreement and provided that such use does not include or rely upon any Protected Intellectual Property of Kaleida Ltd.
For the avoidance of doubt, where separation between Protected Intellectual Property and Custom / Included Intellectual Property is unclear, this file shall be treated as containing Protected Intellectual Property.
*/

// IP-NOTICE: MIXED
// TODO refactor to use local storage?
// Cookies are sent with every request, more secure to only send in the way intended, i.e as bearer token
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Constants } from 'app/core/models/constants';
import { getAdjustedCurrentDate } from 'app/shared/helpers/date-helpers';
import { environment } from 'environments/environment';
import { map, share } from 'rxjs/operators';
import { AuthResponse } from './auth-response.model';
import { Observable, throwError } from 'rxjs';




// TODO rename this service to be password credential flow specific
// TODO implmenet interface with auth and deauth methods for app auth service to consume
@Injectable({providedIn: 'root'})
export class ApiAuthService {

  // private clientId: number = 1; //TODO configuration file setting
  // private clientSecret: string = 'aaaaaaaa-dfb2-4cf8-8151-d85b41cd7be1'; //TODO configuration file setting
  // private baseApiUrl: string = 'http://localhost/Kaleida.solutionsignoff.WebService'; //TODO configuration file setting


  protected clientSecret: string = environment.clientSecret;
  protected readonly apiPrefix: string = environment.apiPrefix;
  protected readonly baseApiUrl: string = environment.baseUrl;
  protected apiUrl: string = this.baseApiUrl + this.apiPrefix;

  accessToken = '';
  refreshToken = '';

  constructor(private http: HttpClient, private router: Router) {
  }

  public async authenticate(username: string, password: string, otp: string): Promise<AuthResponse> {
    var accessTokenRequest = this.requestNewAccessToken(username, password, otp);
    return await this.authenticate_handleAccessTokenResponse(accessTokenRequest);
  }

  public async onboardAndAuthenticate(userId: number, onboardEmailLinkToken: string, otp: string): Promise<AuthResponse> {
    const url = this.apiUrl + '/organiser-onboarding/onboard-and-log-in';
    console.debug(`About to onboard user and request Access Token with ${url}`);

    var body = new HttpParams()
    //.set('grant_type', 'password')  // not sure on best value but don't think this is used?
    .set('clientSecret', this.clientSecret)
    .set('userId', userId)
    .set('onboardEmailLinkToken', onboardEmailLinkToken)
    .set('otp', otp)

    var accessTokenRequest = this.http.post(url, body).toPromise();
    return await this.authenticate_handleAccessTokenResponse(accessTokenRequest);
  }

  public async authenticateViaMicrosoft(msGraphAccessToken: string): Promise<AuthResponse> {
    var accessTokenRequest = this.requestNewAccessTokenViaMicrosoft(msGraphAccessToken);
    return await this.authenticate_handleAccessTokenResponse(accessTokenRequest);
  }


  public async authenticate_handleAccessTokenResponse(pendingRequest: Promise<any>): Promise<AuthResponse> {

    const result: AuthResponse = new AuthResponse();

    this.accessToken = '';
    this.refreshToken = '';

    await pendingRequest
      .then((res:any) => {
        const expiryDate = getAdjustedCurrentDate(res.expires_in);
        console.debug(`Access token retrieved: Expires in ${res.expires_in} seconds @ ${expiryDate}`);
        result.data = res.data;
        this.accessToken = result.data.token;
        this.refreshToken = result.data.refreshToken;
        localStorage.setItem(Constants.LocalStorage.KeyAccessToken, this.accessToken);
        localStorage.setItem(Constants.LocalStorage.KeyRefreshToken, this.refreshToken);
      })
      .catch((error) => {
        result.isError = true;
        result.resetToFirstPageOfLogin = error.error?.resetToFirstPageOfLogin ?? true;

        // TODO If response is not JSON, return some error general information manually based on http status code
        if (error.status === 404) {
          result.errorType = 'Not Found';
          result.errorMessage = 'HTTP Error 404. The requested resource is not found.';
        } else if (error.status === 400) {
          result.errorType = 'Bad Request';
          result.errorMessage = error.error.message;
        } else if (error.status === 0) {
          result.errorType = 'Unknown Error';
          result.errorMessage = error.message;
        } else {
          /* copied from previous authentication method but doesn't work, there is no _body member
          const body = JSON.parse(error._body);

          result.errorType = body.error;
          result.errorMessage = body.message;
          */
         
          // alternative based on fields actually present
          result.errorType = error.statusText;
          result.errorMessage = error.message;

          // if it's a license not found error though, we'll pass through the exact message and let the caller handle it
          if(error.error?.Errors!=null && error.error?.Errors.length>1)
          {
            if(error.error.Errors[0]=="LicenseNotFoundException")
            {
              result.errorType = error.error.Errors[0];
              result.errorMessage = error.error.Errors[1];
            }
          }
        }
      });

    return Promise.resolve(result);
  }

  public deauthenticate(): Promise<boolean> {

    this.accessToken = '';
    this.refreshToken = '';
    return Promise.resolve(true);
  }

  public isAuthenticated(): boolean {

    if (this.accessToken.length > 0) {
      return true;
    }
    return this.checkCookie();
  }

  private checkCookie(): boolean {
    const accessTokenFromCookie = localStorage.getItem(Constants.LocalStorage.KeyAccessToken);

    if (accessTokenFromCookie !== null && accessTokenFromCookie !== undefined && accessTokenFromCookie.length > 0) {
      console.debug('Retrieved Access Token from local storage');
      this.accessToken = accessTokenFromCookie;
      return true;
    }
    return false;
  }

  private requestNewAccessToken(username: string, password: string, otp: string): Promise<Object> {
    const url = this.apiUrl + '/token';
    console.debug(`About to request new Access Token with ${url}`);

    var body = new HttpParams()
    .set('grant_type', 'password')
    .set('clientSecret', this.clientSecret)
    .set('username', username)
    .set('password', password)
    .set('preventCustomerOnly', true);

    if(otp!=null) body=body.set('otp', otp);

    return this.http.post(url, body).toPromise();
  }

  private requestNewAccessTokenViaMicrosoft(msGraphAccessToken: string): Promise<Object> {
    const url = this.apiUrl + '/login-via-ms';
    const body = new HttpParams().set('clientsecret', this.clientSecret).set('msGraphAccessToken', msGraphAccessToken);
    return this.http.post(url, body).toPromise();
  }

  refreshAccessToken(): Observable<string> {
    const url = this.apiUrl + '/refresh-token';
    const refreshToken = localStorage.getItem(Constants.LocalStorage.KeyRefreshToken);

    console.debug(`refresh access token redirect url = ${this.router.url}`)

    localStorage.setItem(Constants.LocalStorage.RedirectUrl, this.router.url);

    if (!refreshToken) {
      return throwError(() => new HttpErrorResponse({ status: 401, statusText: 'Missing refresh token' }));
    }
  
    const body = new HttpParams().set('grant_type', 'refresh_token').set('refresh_token', refreshToken);
    const result: AuthResponse = new AuthResponse();

    return this.http.post(url, body).pipe(
      share(),
      map((res:any) => {
        result.data = res.data;
        this.accessToken = result.data.token;
        this.refreshToken = result.data.refreshToken;
        localStorage.setItem(Constants.LocalStorage.KeyAccessToken, this.accessToken);
        localStorage.setItem(Constants.LocalStorage.KeyRefreshToken, this.refreshToken);
        localStorage.setItem(Constants.LocalStorage.RedirectUrl, '');

        return this.accessToken;
      })
    );
  }

  getAccessToken(): string {
    const accessTokenFromCookie = localStorage.getItem(Constants.LocalStorage.KeyAccessToken);
    return accessTokenFromCookie;
  }
}
